import type { BlockedTime, StaffMember, StaffRole } from "../types";
import { intToWeekDayLabel } from "../utils";
import type { StaffDetailsRpcRow, StaffListRpcRow } from "./types";

// ============================================================================
// Pure transform layer between the RPC response shapes and the domain model.
// No Supabase calls, no side effects — safe to unit-test with fixtures.
// ============================================================================

/**
 * DB role name → UI `StaffRole` label. The DB stores English canonical
 * names; the UI displays German human labels.
 */
const ROLE_NAME_MAP: Record<string, StaffRole> = {
  "Super Admin": "Super-Admin",
  Admin: "Admin",
  Teacher: "Lehrkraft",
  Lehrkraft: "Lehrkraft",
  "Erzieher*innen": "Erzieher*in (eFöB/Hort)",
  Hausmeister: "Hausmeister",
  Secretary: "Sekretariat",
  Sekretariat: "Sekretariat",
};

/**
 * Opposite of `ROLE_NAME_MAP` — the DB canonical name the RPCs expect when
 * the client submits a role. Shared between `createStaffMember` and
 * `updateStaffMember` so both stay in sync.
 */
export const UI_ROLE_TO_DB: Record<string, string> = {
  "Super-Admin": "Super Admin",
  Admin: "Admin",
  Lehrkraft: "Teacher",
  "Erzieher*in (eFöB/Hort)": "Erzieher*innen",
  Hausmeister: "Hausmeister",
  Sekretariat: "Secretary",
  "Sonstige Fachkraft": "Teacher",
};

export function mapRoleName(dbRole: string | null): StaffRole {
  if (!dbRole) return "Lehrkraft";
  return ROLE_NAME_MAP[dbRole] || "Sonstige Fachkraft";
}

/**
 * Format a class-link row from `fn_staff_get_details.class_links` into the
 * human-readable badge label shown in the expanded row.
 */
export function formatClassLink(link: {
  role: string | null;
  class_name: string;
  is_class_teacher: boolean;
}): string {
  const isHead =
    link.is_class_teacher === true || link.role === "Klassenleitung";
  return isHead
    ? `Klassenleitung ${link.class_name}`
    : `Klassen begleiten ${link.class_name}`;
}

/**
 * Convert a slim list RPC row into a fresh `StaffMember`. Detail fields
 * (contact email/phone, Deputat, subjects, blocked times, class links) are
 * populated by `mergeDetailsIntoMember` on row expansion.
 */
export function rpcRowToStaffMember(row: StaffListRpcRow): StaffMember {
  const role = mapRoleName(row.role_name);
  const sm: StaffMember = {
    id: row.id,
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    // List payload deliberately omits contact details — they arrive via
    // fn_staff_get_details on row expansion. Leave as empty placeholders so
    // the StaffMember shape stays the same for existing consumers.
    email: "",
    authEmail: undefined,
    bannedUntil: row.banned_until || null,
    phone: "",
    role,
    tasks: row.tasks || "",
    status: row.status === "active" ? "aktiv" : "inaktiv",
    // Server-computed — replaces drift-prone `accountStatus === 'active'` check.
    accountStatus: row.has_login ? "active" : "none",
    twoFactorEnabled: false, // managed by auth system
    blockedTimes: [],
    responsibleClasses: [], // lazy — populated by fn_staff_get_details
    permissions: (row.permissions || []) as StaffMember["permissions"],
    kurzung: row.kurzung || "",
    schoolId: row.school_id,
    detailsLoaded: false,
  };

  if (role === "Lehrkraft") {
    sm.teacherInfo = {
      weeklyHours: 0,
      creditHours: 0,
      ageReduction: 0,
      creditHoursNote: "",
      studiedSubjects: [],
      studiedSubjectNames: [],
      preferredSubjects: [],
      preferredClasses: [],
      otherWishes: "",
    };
  } else if (role === "Erzieher*in (eFöB/Hort)") {
    sm.educatorInfo = {
      weeklyHours: 0,
      additionalQualification: "",
      preferredClasses: [],
      otherWishes: "",
    };
  }

  return sm;
}

/**
 * Merge a `fn_staff_get_details` payload into an existing StaffMember,
 * returning a new object. The list row (id, name, role, permissions, etc.)
 * is preserved; only detail fields are overwritten.
 */
export function mergeDetailsIntoMember(
  member: StaffMember,
  details: StaffDetailsRpcRow,
): StaffMember {
  const blockedTimes: BlockedTime[] = (details.blocked_times || []).map(
    (bt) => ({
      id: bt.id,
      day: intToWeekDayLabel(bt.weekday),
      isFullDay: bt.is_full_day,
      startTime: bt.start_time || undefined,
      endTime: bt.end_time || undefined,
      type: bt.type,
      reason: bt.reason || undefined,
    }),
  );
  const next: StaffMember = {
    ...member,
    email: details.contact_email || "",
    authEmail: details.auth_email || undefined,
    bannedUntil: details.banned_until || null,
    // Keep accountStatus in sync with the details response. A permanent
    // revoke drops the auth.users row, so auth_email comes back null; we
    // reflect that here so the list badge + button logic immediately
    // downgrade to "Kein Login" without waiting for a full bundle refetch.
    accountStatus: details.auth_email ? "active" : "none",
    phone: details.phone || "",
    blockedTimes,
    responsibleClasses: (details.class_links || []).map(formatClassLink),
    detailsLoaded: true,
  };

  if (member.role === "Lehrkraft") {
    next.teacherInfo = {
      weeklyHours: details.hours_account || 0,
      creditHours: details.credit_hours || 0,
      ageReduction: details.age_reduction || 0,
      creditHoursNote: details.credit_hours_note || "",
      studiedSubjects: Array.isArray(details.subjects_stud)
        ? details.subjects_stud
        : [],
      studiedSubjectNames: Array.isArray(details.studied_subjects)
        ? details.studied_subjects.map((s) => s.name).filter(Boolean)
        : [],
      preferredSubjects: [],
      preferredClasses: [],
      otherWishes: details.wishes || "",
    };
  } else if (member.role === "Erzieher*in (eFöB/Hort)") {
    next.educatorInfo = {
      weeklyHours: details.hours_account || 0,
      additionalQualification: "",
      preferredClasses: [],
      otherWishes: details.wishes || "",
    };
  }

  return next;
}
